import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader2, ExternalLink, Edit2, Eye, EyeOff } from 'lucide-react';
import { clientsAPI } from '../lib/api';
import { Button } from './ui/button';
import { useNavigate } from 'react-router-dom';

/* ---------- formatadores (espelhados do ClientForm) ---------- */
const formatCPF = (v) => v.replace(/\D/g, '')
  .replace(/(\d{3})(\d)/, '$1.$2')
  .replace(/(\d{3})(\d)/, '$1.$2')
  .replace(/(\d{3})(\d{1,2})/, '$1-$2')
  .replace(/(-\d{2})\d+?$/, '$1');

const formatCNPJ = (v) => v.replace(/\D/g, '')
  .replace(/(\d{2})(\d)/, '$1.$2')
  .replace(/(\d{3})(\d)/, '$1.$2')
  .replace(/(\d{3})(\d)/, '$1/$2')
  .replace(/(\d{4})(\d{1,2})/, '$1-$2')
  .replace(/(-\d{2})\d+?$/, '$1');

const formatCpfCnpj = (v) => {
  if (!v) return '';
  const n = String(v).replace(/\D/g, '');
  return n.length <= 11 ? formatCPF(String(v)) : formatCNPJ(String(v));
};

const formatPhone = (v) => {
  if (!v) return '';
  const n = String(v).replace(/\D/g, '');
  // remove DDI 55 se vier no começo
  const clean = n.startsWith('55') && n.length > 11 ? n.slice(2) : n;
  return clean
    .replace(/(\d{2})(\d)/, '($1) $2')
    .replace(/(\d{5})(\d)/, '$1-$2')
    .replace(/(-\d{4})\d+?$/, '$1');
};

const formatDate = (v) => {
  if (!v) return '';
  // aceita 'YYYY-MM-DD' ou ISO
  try {
    const d = typeof v === 'string' && v.length === 10 ? new Date(`${v}T00:00:00`) : new Date(v);
    if (isNaN(d.getTime())) return v;
    return d.toLocaleDateString('pt-BR');
  } catch {
    return v;
  }
};

/**
 * Modal de visualização completa do cliente (Item 8).
 * Renderizado via Portal direto no document.body para evitar quebra de
 * position:fixed por ancestrais com transform/overflow.
 */
const ClientQuickViewModal = ({ clientId, onClose }) => {
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSenhaGov, setShowSenhaGov] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!clientId) return;
    setLoading(true);
    clientsAPI.get(clientId)
      .then((res) => setClient(res.data))
      .catch(() => setClient(null))
      .finally(() => setLoading(false));
  }, [clientId]);

  // Travar scroll do body enquanto modal aberto
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // ESC fecha
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!clientId) return null;
  if (typeof document === 'undefined') return null;

  const Field = ({ label, value, full, mono }) => {
    if (value === undefined || value === null || value === '') return null;
    return (
      <div className={full ? 'sm:col-span-2' : ''}>
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">{label}</p>
        <p className={`text-sm break-words ${mono ? 'font-mono' : ''}`}>{value}</p>
      </div>
    );
  };

  // Detecta PJ pelo CPF/CNPJ
  const cpfLen = (client?.cpf || '').replace(/\D/g, '').length;
  const isPJ = cpfLen > 11;

  return createPortal(
    <div
      className="fixed inset-0 z-[100] bg-black/70 flex items-start sm:items-center justify-center p-0 sm:p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="client-quickview-overlay"
      style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
    >
      <div
        className="bg-card rounded-none sm:rounded-xl w-full max-w-4xl shadow-2xl my-0 sm:my-8 flex flex-col overflow-hidden border min-h-screen sm:min-h-0 sm:max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-4 border-b flex items-center justify-between bg-muted/30 sticky top-0 z-10 bg-card">
          <div>
            <h2 className="text-lg font-bold">Cadastro do Cliente</h2>
            <p className="text-xs text-muted-foreground">Visualização completa</p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-muted rounded-lg"
            data-testid="btn-close-client-quickview"
            aria-label="Fechar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-7 h-7 animate-spin text-primary" />
            </div>
          ) : !client ? (
            <p className="text-center text-muted-foreground py-10">Cliente não encontrado.</p>
          ) : (
            <div className="space-y-5">
              {/* Cabeçalho */}
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                  <h3 className="text-2xl font-bold" data-testid="client-quickview-nome">{client.nome_completo}</h3>
                  <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-primary/10 text-primary">
                      {isPJ ? 'Pessoa Jurídica' : 'Pessoa Física'}
                    </span>
                    {client.tem_projeto_ativo && (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-100 text-emerald-700">
                        Projeto Ativo
                      </span>
                    )}
                    {client.tem_proposta_aberta && (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-blue-100 text-blue-700">
                        Proposta Aberta
                      </span>
                    )}
                    {client.parceiro_nome && (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-100 text-amber-700">
                        Parceiro: {client.parceiro_nome}
                      </span>
                    )}
                  </div>
                </div>
                <Button
                  size="sm"
                  onClick={() => { onClose(); navigate(`/clientes/${client.id}`); }}
                  data-testid="btn-abrir-cliente-completo"
                  className="gap-1.5"
                >
                  <Edit2 className="w-3.5 h-3.5" />
                  Editar Cadastro
                </Button>
              </div>

              {/* Dados Pessoais */}
              <section className="border rounded-lg p-4 bg-muted/10">
                <h4 className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-3">
                  Dados {isPJ ? 'da Empresa' : 'Pessoais'}
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="Nome / Razão Social" value={client.nome_completo} full />
                  <Field label={isPJ ? 'CNPJ' : 'CPF'} value={formatCpfCnpj(client.cpf)} mono />
                  <Field label={isPJ ? 'Data de Abertura' : 'Data de Nascimento'} value={formatDate(client.data_nascimento)} />
                  <Field label="Telefone / WhatsApp" value={formatPhone(client.telefone)} mono />
                  <Field label="Parceiro" value={client.parceiro_nome} />
                </div>
              </section>

              {/* Endereço */}
              {(client.endereco || client.cidade || client.estado || client.bloco || client.complemento) && (
                <section className="border rounded-lg p-4 bg-muted/10">
                  <h4 className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-3">
                    Endereço
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Field label="Endereço" value={client.endereco} full />
                    <Field label="Bloco / Lote" value={client.bloco} />
                    <Field label="Complemento" value={client.complemento} />
                    <Field label="Cidade" value={client.cidade} />
                    <Field label="Estado / UF" value={client.estado} />
                  </div>
                </section>
              )}

              {/* Áreas */}
              {(client.area_total || client.area_construida) && (
                <section className="border rounded-lg p-4 bg-muted/10">
                  <h4 className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-3">
                    Áreas
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Field label="Área Total" value={client.area_total ? `${client.area_total} ha` : null} />
                    <Field label="Área Construída" value={client.area_construida ? `${client.area_construida} ha` : null} />
                  </div>
                </section>
              )}

              {/* Dados Bancários */}
              {(client.banco || client.agencia || client.conta) && (
                <section className="border rounded-lg p-4 bg-muted/10">
                  <h4 className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-3">
                    Dados Bancários
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <Field label="Banco" value={client.banco} />
                    <Field label="Agência" value={client.agencia} mono />
                    <Field label="Conta" value={client.conta} mono />
                  </div>
                </section>
              )}

              {/* Acesso Gov */}
              {(client.usuario_gov || client.senha_gov) && (
                <section className="border rounded-lg p-4 bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-900">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-xs uppercase tracking-wider font-semibold text-amber-700 dark:text-amber-400">
                      Acesso Gov.br
                    </h4>
                    {client.senha_gov && (
                      <button
                        type="button"
                        onClick={() => setShowSenhaGov((s) => !s)}
                        className="text-xs text-amber-700 dark:text-amber-400 hover:underline flex items-center gap-1"
                        data-testid="btn-toggle-senha-gov"
                      >
                        {showSenhaGov ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                        {showSenhaGov ? 'Ocultar' : 'Mostrar'} senha
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Field label="Usuário" value={client.usuario_gov} mono />
                    <Field
                      label="Senha"
                      value={client.senha_gov ? (showSenhaGov ? client.senha_gov : '•'.repeat(Math.min(client.senha_gov.length, 12))) : null}
                      mono
                    />
                  </div>
                </section>
              )}

              {/* Metadados */}
              <section className="border rounded-lg p-4 bg-muted/10">
                <h4 className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-3">
                  Cadastro
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="ID" value={client.id} mono />
                  <Field label="Criado em" value={client.created_at ? new Date(client.created_at).toLocaleString('pt-BR') : null} />
                  <Field label="Criado por" value={client.criado_por_nome} />
                  <Field label="Último alerta" value={client.ultimo_alerta ? new Date(client.ultimo_alerta).toLocaleString('pt-BR') : null} />
                </div>
              </section>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t flex justify-end gap-2 bg-muted/20 sticky bottom-0 bg-card">
          <Button variant="outline" size="sm" onClick={onClose} data-testid="btn-fechar-client-quickview">
            Fechar
          </Button>
          {client && (
            <Button
              size="sm"
              variant="default"
              onClick={() => { onClose(); navigate(`/clientes/${client.id}`); }}
              className="gap-1.5"
              data-testid="btn-abrir-pagina-completa"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              Abrir Página Completa
            </Button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ClientQuickViewModal;
